import {
  ACTION,
  Get,
  IconName,
  canTraderAdminForceCancelCustomerOrder,
  filterByCellValueMenuItem,
  useCallbackRef,
  useDisclosureWithContext,
  useEndpointsContext,
  useGlobalToasts,
  useMixpanel,
  type CustomerOrder,
  type DataResponse,
  type FilterableProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { manualRFQPricingThunks } from '../../../../components/OMS/ManualRFQPricing/ManualRFQPricingSlice';
import { useFeatureFlag, useRoleAuth } from '../../../../hooks';
import { useAppLayoutId } from '../../../../hooks/useAppLayoutId';
import { useAppStateDispatch } from '../../../../providers/AppStateProvider';

import { ModifyFixingDialog, type ModifyFixingProps } from '../../../Blotters/CareOrders/Dialogs/ModifyFixingDialog';
import { CancelS2FCustomerOrderDialog } from '../../../Blotters/CustomerOrders/Dialogs/CancelS2FCustomerOrderDialog';
import { useCopyCustomerOrderTextMenuItem } from '../../../Blotters/CustomerOrders/useCopyCustomerOrderTextMenuItem';
import type { ManualRFQ } from './types';
import { colIDToFilterBuilderKey } from './useManualRFQsBlotterFilter';

export function useManualRFQsBlotterMenu({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}) {
  const dispatch = useAppStateDispatch();
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useEndpointsContext();
  const { isAuthorized } = useRoleAuth();
  const navigate = useNavigate();
  const layoutId = useAppLayoutId('trading');

  const mixpanel = useMixpanel();
  const { allowCancellingS2FCustomerOrder, enableMakerModifyFixing, enableModifyFixingDialogV2 } = useFeatureFlag();

  const showModifyFixing = isAuthorized(ACTION.EDIT_ORDER_FIXING_DETAILS) && enableMakerModifyFixing;

  const showForceCancelOrderOption =
    allowCancellingS2FCustomerOrder && isAuthorized(ACTION.FORCE_CANCEL_SPREADTOFIXING_ORDER);
  const forceCancelCustomerOrderDialog = useDisclosureWithContext<string>();

  const modifyFixingDialog = useDisclosureWithContext<ModifyFixingProps>();

  const copyCustomerOrderExecutionTextMenuItem = useCopyCustomerOrderTextMenuItem();

  /**
   * Given an RFQID from a ManualRFQ, makes a request to the backend and returns the OrderID for the CustomerOrder
   * associated with this ManualRFQ.
   */
  const getCustomerOrderID = useCallback(
    (rfqid: string) => {
      if (!orgApiEndpoint) {
        return undefined;
      }

      return Get<DataResponse<CustomerOrder>>(orgApiEndpoint, `/customer/orders?RFQID=${rfqid}`)
        .then(res => {
          return res.data.at(0)?.OrderID;
        })
        .catch((e: ErrorEvent) => {
          addToast({
            variant: 'NEGATIVE',
            text: `Failed to find order to cancel: ${e.message}`,
          });
        });
    },
    [orgApiEndpoint, addToast]
  );

  const handleForceCancelOrder = useCallback(
    async (rfqid: string) => {
      const orderID = await getCustomerOrderID(rfqid);
      if (orderID) {
        forceCancelCustomerOrderDialog.open(orderID);
      }
    },
    [getCustomerOrderID, forceCancelCustomerOrderDialog]
  );

  const handleModifyFixing = useCallback(
    async (quote: ManualRFQ) => {
      const customerOrderID = await getCustomerOrderID(quote.RFQID);
      if (customerOrderID) {
        modifyFixingDialog.open({
          mode: 'maker',
          quote,
          orderID: customerOrderID,
        });
      }
    },
    [getCustomerOrderID, modifyFixingDialog]
  );

  const handleOpenPricingPanel = useCallback(
    (quote: ManualRFQ) => {
      dispatch(manualRFQPricingThunks.prime({ quote, navigate, layoutId }));
    },
    [dispatch, navigate, layoutId]
  );

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<ManualRFQ>) => {
    const data = params?.node?.data;
    if (data == null) {
      return [];
    }

    return compact([
      ...filterByCellValueMenuItem({ params, filterableProperties, openClause, colIDToFilterBuilderKey, mixpanel }),
      copyCustomerOrderExecutionTextMenuItem(data),
      data.isMultitoken && {
        name: 'Show Quoting Panel',
        action: () => handleOpenPricingPanel(data),
        icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
      },
      showForceCancelOrderOption && {
        name: 'Force cancel order',
        action: () => handleForceCancelOrder(data.RFQID),
        disabled: !canTraderAdminForceCancelCustomerOrder(data),
      },
      showModifyFixing &&
        data.canModifyFixing && {
          name: 'Modify Fixing',
          action: () => handleModifyFixing(data),
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
          disabled: data.isMultitoken && !enableModifyFixingDialogV2,
        },
    ]);
  });

  const dialogs = useMemo(
    () => (
      <>
        <CancelS2FCustomerOrderDialog {...forceCancelCustomerOrderDialog} />
        <ModifyFixingDialog {...modifyFixingDialog} />
      </>
    ),
    [forceCancelCustomerOrderDialog, modifyFixingDialog]
  );

  return {
    getCustomContextMenuItems,
    dialogs,
  };
}
